import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { useApi } from '@/lib/api-context';

import {
  analysisQuery,
  complianceQuery,
  throughputDocumentsQuery,
  throughputQuery,
} from './presentation';
import {
  complianceResponseSchema,
  departmentComplianceResponseSchema,
  deviationResponseSchema,
  myStagesResponseSchema,
  reworkSummaryResponseSchema,
  standardsMatrixResponseSchema,
  throughputDocumentsResponseSchema,
  throughputResponseSchema,
  type Axis,
  type Compliance,
  type DepartmentCompliance,
  type Deviation,
  type PeriodPick,
  type MyStage,
  type ReworkSummary,
  type StandardsRow,
  type Throughput,
  type ThroughputDocuments,
  type TimeStudyFilters,
} from './types';

// Consultas de Estudio de tiempos (so=420). La empresa activa va en la posición 2 del
// queryKey: nunca se sirve la caché de otra empresa. Cada respuesta pasa por Zod.

const Q = '/api/v1/quality';

interface Scope {
  companyId: string | null;
  enabled: boolean;
}

interface Freshness {
  staleTime?: number;
  refetchOnMount?: 'always';
  refetchInterval?: number;
}

function useParsed<T>(
  key: readonly unknown[],
  url: string,
  parse: (raw: unknown) => T,
  { companyId, enabled }: Scope,
  freshness: Freshness = {},
): UseQueryResult<T> {
  const api = useApi();
  return useQuery({
    queryKey: ['time-study', companyId, ...key, url],
    queryFn: async () => parse(await api.get<unknown>(url)),
    enabled,
    ...freshness,
  });
}

/** Tiempos en curso: se releen al volver a la pantalla y cada minuto mientras se mira. */
const LIVE: Freshness = { staleTime: 0, refetchOnMount: 'always', refetchInterval: 60_000 };

export function useDeviation(
  groupBy: 'stage' | Axis | 'documentType,stage',
  f: TimeStudyFilters,
  scope: Scope,
): UseQueryResult<Deviation> {
  const url = `${Q}/productivity/deviation?groupBy=${groupBy}&${analysisQuery(f)}`;
  return useParsed(['deviation'], url, (r) => deviationResponseSchema.parse(r).data, scope);
}

export function useReworkSummary(f: TimeStudyFilters, scope: Scope): UseQueryResult<ReworkSummary> {
  const url = `${Q}/rework/summary?${analysisQuery(f)}`;
  return useParsed(['rework'], url, (r) => reworkSummaryResponseSchema.parse(r).data, scope);
}

export function useThroughput(f: TimeStudyFilters, scope: Scope): UseQueryResult<Throughput> {
  const url = `${Q}/productivity/throughput?${throughputQuery(f)}`;
  return useParsed(['throughput'], url, (r) => throughputResponseSchema.parse(r).data, scope);
}

export function useCompliance(f: TimeStudyFilters, scope: Scope): UseQueryResult<Compliance> {
  const url = `${Q}/productivity/compliance?${complianceQuery(f)}`;
  return useParsed(['compliance'], url, (r) => complianceResponseSchema.parse(r).data, scope);
}

export function useComplianceByDepartment(
  f: TimeStudyFilters,
  scope: Scope,
): UseQueryResult<DepartmentCompliance> {
  const url = `${Q}/productivity/compliance/departments?${complianceQuery(f)}`;
  return useParsed(
    ['compliance-departments'],
    url,
    (r) => departmentComplianceResponseSchema.parse(r).data,
    scope,
  );
}

export function useThroughputDocuments(
  f: TimeStudyFilters,
  pick: PeriodPick,
  scope: Scope,
): UseQueryResult<ThroughputDocuments> {
  const url = `${Q}/productivity/throughput/documents?${throughputDocumentsQuery(f, pick)}`;
  return useParsed(
    ['throughput-documents'],
    url,
    (r) => throughputDocumentsResponseSchema.parse(r).data,
    scope,
  );
}

export function useMyStages(scope: Scope): UseQueryResult<MyStage[]> {
  const url = `${Q}/time-engine/my-stages?limit=20`;
  return useParsed(
    ['my-stages'],
    url,
    (r) => myStagesResponseSchema.parse(r).data.items,
    scope,
    LIVE,
  );
}

export function useStandardsMatrix(scope: Scope): UseQueryResult<StandardsRow[]> {
  const url = `${Q}/production-standards`;
  return useParsed(['standards'], url, (r) => standardsMatrixResponseSchema.parse(r).data, scope);
}
